import React, { createContext, useContext, useEffect, useState } from "react";

interface ResponsiveContextType {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeScreen: boolean;
  screenSize: "mobile" | "tablet" | "desktop" | "large";
  width: number;
  height: number;
}

const ResponsiveContext = createContext<ResponsiveContextType | undefined>(
  undefined,
);

export const useResponsive = () => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }
  return context;
};

interface ResponsiveProviderProps {
  children: React.ReactNode;
}

export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({
  children,
}) => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  const [height, setHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800,
  );

  const isMobile = width < 640; // < sm
  const isTablet = width >= 640 && width < 1024; // sm to lg
  const isDesktop = width >= 1024 && width < 1536; // lg to 2xl
  const isLargeScreen = width >= 1536; // >= 2xl

  const screenSize: "mobile" | "tablet" | "desktop" | "large" = isLargeScreen
    ? "large"
    : isDesktop
      ? "desktop"
      : isTablet
        ? "tablet"
        : "mobile";

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    // Initial setup
    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const value: ResponsiveContextType = {
    isMobile,
    isTablet,
    isDesktop,
    isLargeScreen,
    screenSize,
    width,
    height,
  };

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
};

// Hook for responsive utilities
export const useResponsiveClasses = (classes: {
  mobile?: string;
  tablet?: string;
  desktop?: string;
  large?: string;
  default?: string;
}) => {
  const { screenSize } = useResponsive();

  const classNames = [classes.default || "", classes[screenSize] || ""]
    .filter(Boolean)
    .join(" ");

  return classNames;
};
